/**
 * Release tour — Reports.
 *
 * Anchors are real `data-tour` attributes on `reports-category-page.tsx` — the
 * landing launchpad at `/reports`. Copy is deliberately honest about what's real
 * today: 7 native, cross-filtering "Dashboards" reports (Revenue, Top Procedures,
 * Top Diagnosis, Top Reason, Avg Time, Payment Cash & Card, Outstanding Payment)
 * built on 7 real chart types (donut/bar/area/waterfall/treemap/gauge/tornado),
 * reused across ~11 report pages — plus the pre-existing backend-driven "Charts"
 * and "Data Lists" catalog. No RBAC gate exists yet (PROD-378, deferred) and the
 * new native dashboards have no export button — don't claim either.
 *
 * Mount at the top of `ReportsCategoryPage`:
 *   const tour = useReleaseTour(REPORTS_TOUR, { userRole: user.role });
 *   return <div>...<ReleaseTour {...tour} /></div>;
 */
import type { ReleaseTourConfig } from '../release-tour-types';
import { registerReleaseTour } from '../release-tour-registry';

export const REPORTS_TOUR: ReleaseTourConfig = {
  id: 'reports-v1',
  name: 'Reports',
  enabled: true,
  steps: [
    {
      target: '[data-tour="reports-header"]',
      title: 'Introducing the new Reports',
      content:
        'Built on the same new engine — real analytics, not a static export. Click any chart, and every view on the page re-scopes to match.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="reports-tabs"]',
      title: 'Dashboards, Charts, and Data Lists',
      content: 'Interactive dashboards live here. Charts and Data Lists hold the full report catalog your clinic already relies on.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="reports-dashboards-grid"]',
      title: 'Seven dashboards, one click away',
      content:
        'Revenue, Top Procedures, Top Diagnosis, Top Reason for Visit, Average Time, Payment by Method, and Outstanding Balances — each one interactive.',
      placement: 'top',
    },
    {
      target: '[data-tour="reports-revenue-card"]',
      title: 'Start with Revenue',
      content:
        'Slice by specialty, practitioner, practice, billing group, or visit type — click any chart to filter every other chart on the page instantly. This tour won’t show again.',
      placement: 'top',
    },
  ],
};

registerReleaseTour(REPORTS_TOUR);
